import * as React from 'react';
import {
  Shield,
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  Copy,
  ChevronDown,
  ChevronUp,
  Lock,
  Unlock,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TAGADAPAY_PLATFORM_NAME } from '@/lib/branding/platform-name';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { CopyableResourceBadge } from '@/components/ui/copyable-resource-badge';
import { DateDisplay } from '@/components/date-display';
import { PaymentDetails } from '@/lib/api/payments';
import { useToast } from '@/components/ui/use-toast';

interface Payment3DSCardProps {
  payment: PaymentDetails;
}

interface ThreeDSValues {
  id?: string;
  externalSessionId?: string;
  status: string;
  provider?: string;
  type?: string;
  messageVersion?: string;
  acsChallengeMandated?: string | boolean;
  authenticationType?: string;
  authenticationValue?: string;
  eci?: string;
  transStatus?: string;
  transStatusLabel?: string;
  transStatusReason?: string;
  liabilityShifted?: boolean;
  dsTransID?: string;
  acsTransID?: string;
  threeDSServerTransID?: string;
  completedAt?: string;
  createdAt?: string;
  errorCode?: string;
  errorMessage?: string;
  challengePreference?: string;
  challengePreferenceCode?: string;
  interactionCounter?: string;
  whitelistStatus?: string;
  cardBrand?: string;
  source: 'session' | 'extracted';
  processorType?: string;
}

const TRANS_STATUS_MAP: Record<string, { label: string; variant: 'green' | 'destructive' | 'yellow' | 'blue' | 'outline' }> = {
  Y: { label: 'Successful', variant: 'green' },
  A: { label: 'Attempted', variant: 'blue' },
  N: { label: 'Failed', variant: 'destructive' },
  R: { label: 'Rejected', variant: 'destructive' },
  C: { label: 'Challenge', variant: 'yellow' },
  U: { label: 'Unavailable', variant: 'outline' },
  D: { label: 'Decoupled', variant: 'blue' },
  I: { label: 'Info Only', variant: 'outline' },
};

const STATUS_CONFIG: Record<string, { icon: typeof ShieldCheck; color: string; bg: string; border: string }> = {
  authenticated: { icon: ShieldCheck, color: 'text-green-600 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-900/10', border: 'border-green-200 dark:border-green-900' },
  successful: { icon: ShieldCheck, color: 'text-green-600 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-900/10', border: 'border-green-200 dark:border-green-900' },
  succeeded: { icon: ShieldCheck, color: 'text-green-600 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-900/10', border: 'border-green-200 dark:border-green-900' },
  challenged: { icon: Shield, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/10', border: 'border-amber-200 dark:border-amber-900' },
  failed: { icon: ShieldX, color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/10', border: 'border-red-200 dark:border-red-900' },
  error: { icon: ShieldAlert, color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/10', border: 'border-red-200 dark:border-red-900' },
};

function getStatusConfig(status: string) {
  return STATUS_CONFIG[status] || STATUS_CONFIG.challenged;
}

function ValueRow({ label, value, mono = false, highlight = false }: { label: string; value: React.ReactNode; mono?: boolean; highlight?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-muted-foreground text-sm font-medium sm:min-w-[140px]">{label}</span>
      <div className={`flex-1 text-right text-sm ${mono ? 'font-mono' : ''} ${highlight ? 'font-semibold' : ''}`}>
        {value}
      </div>
    </div>
  );
}

export function Payment3DSCard({ payment }: Payment3DSCardProps) {
  const { t } = useTranslation('payments');
  const { toast } = useToast();
  const [showAllIds, setShowAllIds] = React.useState(false);

  const primary = React.useMemo(() => {
    const sessions: ThreeDSValues[] = [];
    const seenIds = new Set<string>();

    const paymentSessions = (payment as any).threedsSessions;
    if (Array.isArray(paymentSessions)) {
      for (const session of paymentSessions) {
        if (session?.id && !seenIds.has(session.id)) {
          seenIds.add(session.id);
          sessions.push({ ...session, source: 'session' as const });
        }
      }
    }

    if (payment.transactions) {
      for (const txn of payment.transactions) {
        const session = (txn as any).threedsSession;
        if (session?.id && !seenIds.has(session.id)) {
          seenIds.add(session.id);
          sessions.push({ ...session, source: 'session' as const, cardBrand: (txn as any).processor?.type });
        }
      }
    }

    if (sessions.length > 0) return sessions[0];

    // Use backend-extracted 3DS values when no formal session exists
    const extracted = (payment as any).extracted3DS;
    if (extracted) {
      return {
        ...extracted,
        source: 'extracted' as const,
      } as ThreeDSValues;
    }

    return null;
  }, [payment]);

  if (!primary) return null;

  const cfg = getStatusConfig(primary.status);
  const StatusIcon = cfg.icon;
  const transStatusInfo = primary.transStatus ? TRANS_STATUS_MAP[primary.transStatus] : null;

  const hasAuthValues = Boolean(primary.eci || primary.authenticationValue);
  const isAuthenticated = primary.transStatus === 'Y' || primary.transStatus === 'A' || primary.status === 'authenticated';
  const isFromExtracted = primary.source === 'extracted';

  const isTagadaStandalone = primary.provider === 'basis_theory' || primary.provider === 'manual' || primary.provider === 'external';
  const isProcessorManaged = !isTagadaStandalone && (Boolean(primary.provider) || isFromExtracted);
  const providerLabel = primary.provider === 'basis_theory' ? 'BasisTheory'
    : primary.provider === 'manual' || primary.provider === 'external' ? 'External'
    : primary.provider === 'extracted' && primary.processorType
      ? primary.processorType.charAt(0).toUpperCase() + primary.processorType.slice(1)
    : primary.provider ? primary.provider.charAt(0).toUpperCase() + primary.provider.slice(1)
    : primary.processorType ? primary.processorType.charAt(0).toUpperCase() + primary.processorType.slice(1)
    : 'Unknown';

  const copyAllValues = () => {
    const lines = [
      primary.eci && `eci: ${primary.eci}`,
      primary.authenticationValue && `authenticationValue: ${primary.authenticationValue}`,
      primary.transStatus && `transStatus: ${primary.transStatus}`,
      primary.transStatusLabel && `transStatusLabel: ${primary.transStatusLabel}`,
      primary.dsTransID && `dsTransID: ${primary.dsTransID}`,
      primary.acsTransID && `acsTransID: ${primary.acsTransID}`,
      primary.messageVersion && `messageVersion: ${primary.messageVersion}`,
      primary.liabilityShifted !== undefined && `liabilityShifted: ${primary.liabilityShifted}`,
      primary.authenticationType && `authenticationType: ${primary.authenticationType}`,
    ].filter(Boolean).join('\n');

    navigator.clipboard.writeText(lines);
    toast({ title: t('threeDS.copied'), description: t('threeDS.copiedDescription') });
  };

  return (
    <Card className={`border ${cfg.border}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <StatusIcon className={`h-5 w-5 ${cfg.color}`} />
            {t('threeDS.title')}
            {isTagadaStandalone ? (
              <Badge variant="blue" className="text-[10px]">{t('threeDS.standalone')}</Badge>
            ) : isProcessorManaged ? (
              <Badge variant="outline" className="text-[10px]">{t('threeDS.processorManaged', { provider: providerLabel })}</Badge>
            ) : null}
          </CardTitle>
          <div className="flex items-center gap-2">
            {hasAuthValues && (
              <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" onClick={copyAllValues}>
                <Copy className="h-3 w-3" />
                {t('threeDS.copyAll')}
              </Button>
            )}
            {primary.id && (
              <CopyableResourceBadge name="3DS Session" value={primary.id} variant="outline" className="text-xs" />
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status Banner */}
        <div className={`flex items-center gap-3 rounded-lg border p-3 ${cfg.bg} ${cfg.border}`}>
          <StatusIcon className={`h-6 w-6 ${cfg.color}`} />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className={`font-semibold ${cfg.color}`}>
                {isAuthenticated ? t('threeDS.status.authenticated') : primary.status === 'challenged' ? t('threeDS.status.challengeRequired') : primary.status}
              </span>
              {transStatusInfo && (
                <Badge variant={transStatusInfo.variant}>
                  {primary.transStatus} — {transStatusInfo.label}
                </Badge>
              )}
              {primary.liabilityShifted !== undefined && (
                <Badge variant={primary.liabilityShifted ? 'green' : 'yellow'} className="gap-1">
                  {primary.liabilityShifted ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
                  {primary.liabilityShifted ? t('threeDS.liabilityShifted') : t('threeDS.noLiabilityShift')}
                </Badge>
              )}
            </div>
            <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
              {primary.messageVersion && <span>3DS v{primary.messageVersion}</span>}
              {primary.authenticationType && <span>• {primary.authenticationType}</span>}
              {primary.provider && primary.provider !== 'extracted' && <span>• {t('threeDS.provider')}: {providerLabel}</span>}
              {primary.acsChallengeMandated && <span>• {t('threeDS.challengeMandated')}</span>}
            </div>
            {!isFromExtracted && (
              <p className="text-muted-foreground mt-1 text-[11px] italic">
                {isTagadaStandalone
                  ? t('threeDS.standaloneDescription')
                  : isProcessorManaged
                    ? t('threeDS.processorManagedDescription', { provider: providerLabel })
                    : null}
              </p>
            )}
          </div>
        </div>

        {/* Key Authentication Values */}
        {hasAuthValues && (
          <div className="space-y-3">
            <h4 className="text-sm font-semibold">{t('threeDS.authenticationValues')}</h4>
            <div className="rounded-lg border bg-muted/30 p-3">
              <div className="space-y-2.5">
                {primary.eci && (
                  <ValueRow label={t('threeDS.eci')} highlight value={
                    <CopyableResourceBadge name="ECI" value={primary.eci} variant="outline" />
                  } />
                )}
                {primary.authenticationValue && (
                  <ValueRow label={t('threeDS.cavv')} highlight value={
                    <CopyableResourceBadge name="CAVV" value={primary.authenticationValue} variant="outline" />
                  } />
                )}
                {primary.transStatus && (
                  <ValueRow label={t('threeDS.transStatus')} value={
                    <div className="flex items-center justify-end gap-2">
                      {transStatusInfo && <Badge variant={transStatusInfo.variant}>{primary.transStatus}</Badge>}
                      <span className="text-muted-foreground text-xs">
                        {primary.transStatusLabel || transStatusInfo?.label}
                      </span>
                    </div>
                  } />
                )}
                {primary.transStatusReason && (
                  <ValueRow label={t('threeDS.statusReason')} value={<span className="text-muted-foreground">{primary.transStatusReason}</span>} />
                )}
                {primary.dsTransID && (
                  <ValueRow label={t('threeDS.dsTransId')} mono value={
                    <CopyableResourceBadge name="DS Trans" value={primary.dsTransID} variant="outline" className="text-xs" />
                  } />
                )}
                {primary.messageVersion && (
                  <ValueRow label={t('threeDS.version')} value={primary.messageVersion} />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Protocol Details - only show when we have formal session data */}
        {!isFromExtracted && (
          <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
            {primary.messageVersion && (
              <div className="space-y-1">
                <p className="text-muted-foreground text-xs font-medium">{t('threeDS.protocol')}</p>
                <Badge variant="outline">v{primary.messageVersion}</Badge>
              </div>
            )}
            {primary.authenticationType && (
              <div className="space-y-1">
                <p className="text-muted-foreground text-xs font-medium">{t('threeDS.authType')}</p>
                <Badge variant="outline">{primary.authenticationType}</Badge>
              </div>
            )}
          {primary.liabilityShifted !== undefined && primary.liabilityShifted !== null && (
            <div className="space-y-1">
              <p className="text-muted-foreground text-xs font-medium">{t('threeDS.liability')}</p>
              <Badge variant={primary.liabilityShifted ? 'green' : 'yellow'}>
                {primary.liabilityShifted ? t('threeDS.shifted') : t('threeDS.notShifted')}
              </Badge>
            </div>
          )}
          {primary.acsChallengeMandated !== undefined && primary.acsChallengeMandated !== null && (
            <div className="space-y-1">
              <p className="text-muted-foreground text-xs font-medium">{t('threeDS.challenge')}</p>
              <Badge variant={primary.acsChallengeMandated ? 'yellow' : 'outline'}>
                {primary.acsChallengeMandated ? t('threeDS.required') : t('threeDS.notRequired')}
              </Badge>
            </div>
          )}
          </div>
        )}

        {/* Error */}
        {(primary.errorCode || primary.errorMessage) && (
          <>
            <Separator />
            <div className="space-y-2 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-900/10">
              <div className="flex items-center gap-2">
                <ShieldAlert className="h-4 w-4 text-red-600 dark:text-red-400" />
                <span className="text-sm font-semibold text-red-700 dark:text-red-300">{t('threeDS.error')}</span>
                {primary.errorCode && <Badge variant="destructive">{primary.errorCode}</Badge>}
              </div>
              {primary.errorMessage && (
                <pre className="overflow-auto whitespace-pre-wrap text-xs text-red-600 dark:text-red-400">
                  {primary.errorMessage}
                </pre>
              )}
            </div>
          </>
        )}

        {/* Session Identifiers - only when we have a formal session */}
        {!isFromExtracted && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold">{t('threeDS.sessionIdentifiers')}</h4>
              <Button variant="ghost" size="sm" className="h-6 gap-1 text-xs" onClick={() => setShowAllIds(!showAllIds)}>
                {showAllIds ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                {showAllIds ? t('threeDS.less') : t('threeDS.more')}
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {primary.id && (
                <CopyableResourceBadge name={`${TAGADAPAY_PLATFORM_NAME} Session`} value={primary.id} variant="outline" />
              )}
              {primary.externalSessionId && (
                <CopyableResourceBadge name="BT Session" value={primary.externalSessionId} variant="outline" />
              )}
            </div>
            {showAllIds && (
              <div className="mt-2 space-y-2 rounded-lg border bg-muted/30 p-3">
                {primary.dsTransID && (
                  <ValueRow label={t('threeDS.dsTransId')} mono value={
                    <CopyableResourceBadge name="DS Trans" value={primary.dsTransID} variant="outline" />
                  } />
                )}
                {primary.acsTransID && (
                  <ValueRow label={t('threeDS.acsTransId')} mono value={
                    <CopyableResourceBadge name="ACS Trans" value={primary.acsTransID} variant="outline" />
                  } />
                )}
                {primary.threeDSServerTransID && (
                  <ValueRow label={t('threeDS.serverTransId')} mono value={
                    <CopyableResourceBadge name="Server Trans" value={primary.threeDSServerTransID} variant="outline" />
                  } />
                )}
                {primary.completedAt && (
                  <ValueRow label={t('threeDS.completed')} value={<DateDisplay value={primary.completedAt} />} />
                )}
                {primary.createdAt && (
                  <ValueRow label={t('threeDS.created')} value={<DateDisplay value={primary.createdAt} />} />
                )}
                {primary.challengePreference && (
                  <ValueRow label={t('threeDS.challengePreference')} value={primary.challengePreference} />
                )}
                {primary.interactionCounter && (
                  <ValueRow label={t('threeDS.interactionCounter')} value={primary.interactionCounter} />
                )}
                {primary.whitelistStatus && (
                  <ValueRow label={t('threeDS.whitelistStatus')} value={primary.whitelistStatus} />
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
